import React, { ReactNode, createContext, useContext, useMemo, useState } from 'react';

export interface PageOverlayContextValue {
    overlayComponent: ReactNode | null;
    setOverlayComponent: React.Dispatch<React.SetStateAction<React.ReactNode>>;
    showOverlay: boolean;
    setShowOverlay: React.Dispatch<React.SetStateAction<boolean>>;
    openOverlayThenDelay: (delay?: number) => Promise<unknown>;
    delayThenCloseOverlay: (delay?: number) => void;
}

export const PageOverlayContext = createContext<PageOverlayContextValue | undefined>(undefined);

interface PageOverlayProviderProps {
    children: React.ReactNode;
}

export const PageOverlayProvider: React.FC<PageOverlayProviderProps> = ({ children }): ReactNode => {
    const [overlayComponent, setOverlayComponent] = useState<ReactNode | null>(null);
    const [showOverlay, setShowOverlay] = useState(false);

    const contextValue = useMemo(
        () => ({
            overlayComponent,
            setOverlayComponent,
            showOverlay,
            setShowOverlay,
            openOverlayThenDelay: async (delay?: number): Promise<unknown> => {
                return new Promise(resolve => {
                    setShowOverlay(true);
                    setTimeout(resolve, delay ? delay : 500);
                });
            },
            delayThenCloseOverlay: (delay?: number): void => {
                setTimeout(
                    () => {
                        setShowOverlay(false);
                    },
                    delay ? delay : 500
                );
            },
        }),
        [overlayComponent, showOverlay]
    );

    return (
        <PageOverlayContext.Provider value={contextValue}>
            {children}
            {showOverlay && overlayComponent}
        </PageOverlayContext.Provider>
    );
};

export const usePageOverlay = (): PageOverlayContextValue => {
    const context = useContext(PageOverlayContext);
    if (context === undefined) {
        throw new Error('usePageOverlay used while not within PageOverlayProvider.');
    }
    return context;
};
